import type {
  LogActivityDTO,
  SessionLogDTO,
  SessionLogListDTO,
  SessionLogSummaryDTO,
} from "@shared/contracts";
import type {
  LogActivityDayModel,
  SessionLogLineModel,
  SessionLogModel,
  SessionLogPageModel,
  SessionLogSegmentModel,
  SessionLogSplitModel,
  SessionLogSummaryModel,
} from "./logs.model";

/**
 * THE ONLY PLACE THE LOG'S WIRE SHAPE AND THE APP'S SHAPE MEET. Pure, and tested on its own.
 *
 * ⚠️ IT RENAMES ALMOST NOTHING, AND THAT IS NOT A REASON TO DELETE IT. The contract is already
 * camelCase, so a converter here looks redundant right up until the wire gains a field or
 * changes a name — at which point the one place that has to change is this one, rather than
 * every screen that spread a DTO into its props. `createdAt`/`updatedAt` are dropped on
 * purpose: no screen in this feature draws them, and a field the app carries but cannot draw
 * is a field the next author has to work out whether they are allowed to.
 *
 * The 1–5 ratings are passed STRAIGHT THROUGH. `shared/contracts/logs.ts` is emphatic about
 * this: 1–5 is what is tapped, 1–5 is what is stored, 1–5 is what the engine receives, and
 * inventing a 1→10 mapping here would put the stored row and the engine's completion record
 * on different scales inside one transaction.
 */
export function toSessionLog(dto: SessionLogDTO): SessionLogModel {
  return {
    id: dto.id,
    title: dto.title,
    performedOn: dto.performedOn,
    format: dto.format,
    load: dto.load,
    outcome: dto.outcome,
    elapsedSeconds: dto.elapsedSeconds,
    sessionSeconds: dto.sessionSeconds,
    rounds: dto.rounds,
    partialReps: dto.partialReps,
    capHit: dto.capHit,
    performance: dto.performance,
    fatigue: dto.fatigue,
    feeling: dto.feeling,
    rpe: dto.rpe,
    requestId: dto.requestId,
    /* 🚨 THE SEED IS THE SESSION. Every other field is a score; these two are what put the
       workout back on a screen — the history detail redraws its blocks from them, and "do it
       again" starts from them. Null for a hand-logged row and for anything written before the
       columns existed, which is what the screen branches on. */
    seed: dto.seed,
    gear: dto.gear,
    splits: dto.splits.map(toSplit),
    lines: dto.lines.map(toLine),
    segments: dto.segments.map(toSegment),
    /* ⚠️ PASSED THROUGH WHOLE AND UNRENAMED. The trace is thousands of samples; mapping over
       them to produce an identical array would be a copy of a megabyte for no reader's benefit,
       and there is nothing to rename — the wire shape is already what a chart draws. The one
       thing that must NOT happen to it happens nowhere: interpolating across its gaps. */
    heartRate: dto.heartRate,
    isFavourite: dto.isFavourite,
  };
}

function toLine(dto: SessionLogDTO["lines"][number]): SessionLogLineModel {
  return {
    block: dto.block,
    movement: dto.movement,
    movementName: dto.movementName,
    unit: dto.unit,
    modality: dto.modality,
    source: dto.source,
    setIndex: dto.setIndex,
    reps: dto.reps,
    /* KILOS ALL THE WAY THROUGH. Converting to the athlete's unit here would put pounds into a
       model that history, a volume total and a best set all read — and the same number would
       then mean two different things depending on which screen fetched it. */
    loadKg: dto.loadKg,
    feel: dto.feel,
    atSeconds: dto.atSeconds,
    restSeconds: dto.restSeconds,
    /* Null through as null. See the model: an absent prescription is "not known", and a default
       here would turn every older session into a completed one. */
    prescribedLoadKg: dto.prescribedLoadKg,
    prescribedReps: dto.prescribedReps,
    prescribedSets: dto.prescribedSets,
  };
}

function toSegment(dto: SessionLogDTO["segments"][number]): SessionLogSegmentModel {
  return {
    block: dto.block,
    startSeconds: dto.startSeconds,
    endSeconds: dto.endSeconds,
  };
}

function toSplit(dto: SessionLogDTO["splits"][number]): SessionLogSplitModel {
  return { round: dto.round, elapsedSeconds: dto.elapsedSeconds };
}

/**
 * ONE ROW OF HISTORY — added by BUD-290. The same fields, minus the splits a list cannot draw.
 */
export function toSessionLogSummary(dto: SessionLogSummaryDTO): SessionLogSummaryModel {
  return {
    id: dto.id,
    title: dto.title,
    performedOn: dto.performedOn,
    format: dto.format,
    load: dto.load,
    outcome: dto.outcome,
    elapsedSeconds: dto.elapsedSeconds,
    sessionSeconds: dto.sessionSeconds,
    rounds: dto.rounds,
    partialReps: dto.partialReps,
    capHit: dto.capHit,
    performance: dto.performance,
    fatigue: dto.fatigue,
    feeling: dto.feeling,
    rpe: dto.rpe,
    requestId: dto.requestId,
    seed: dto.seed,
    gear: dto.gear,
    isFavourite: dto.isFavourite,
  };
}

/** A page of history with its cursor. The cursor is opaque and is never parsed here. */
export function toSessionLogPage(dto: SessionLogListDTO): SessionLogPageModel {
  return { items: dto.items.map(toSessionLogSummary), nextCursor: dto.nextCursor };
}

/** A year of training days. The civil date stays a string — the grid keys on it. */
export function toLogActivity(dto: LogActivityDTO): LogActivityDayModel[] {
  return dto.days.map((day) => ({
    day: day.day,
    sessions: day.sessions,
    sets: day.sets,
    seconds: day.seconds,
  }));
}

/**
 * 🚨 THE DAY THEY TRAINED, AS A DATE — AND THE ONLY CORRECT WAY TO READ ONE.
 *
 * `performedOn` stays a `YYYY-MM-DD` STRING on both models above, which is right: it is a
 * civil date, the day the athlete names, with no time and no zone in it. But the chart has to
 * do arithmetic on it and every row has to print it, and both need a `Date`.
 *
 * ⚠️ `new Date("2026-08-25")` IS WRONG AND FAILS SILENTLY. The spec parses a bare date-only
 * string as UTC MIDNIGHT, so anywhere west of Greenwich it is the 24th at six in the evening:
 * every session slides a day earlier on the screen whose whole job is to say which day it
 * was, and into the previous week's bar on the chart above it. Handing the three parts to the
 * constructor asks for LOCAL midnight, which is what the day means.
 * `api/src/domains/logs/views.ts` argues the same thing from the other side of the wire, and
 * `logs.converter.test.ts` asserts it with the local getters — the only ones that can tell
 * the two readings apart.
 *
 * IT LIVES IN THE CONVERTER because a converter is the ONE place a wire value becomes a Date;
 * `boiler/dates-only-in-converters` enforces exactly that. `logs.trend.ts` and
 * `use-log-text.ts` call it rather than each parsing a date of their own.
 */
export function civilDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}
